import React, { useState, useEffect } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { T } from "services/lang/LanguageService";
import { Dropdown } from "primereact/dropdown";
import { Chips } from "primereact/chips";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { ReportType } from "api/model/enums";
import { IPeriodLoadingConfig } from "components/dashboard/product-template-table/utils/interfaces";
import { ChronoUnitLegacy } from "utils/time-utils";
import { GeneratedReportTableProps } from "../GeneratedReportTable";


interface ICommonGeneratedReportTableEditorProps {
  config: GeneratedReportTableProps;
  onConfigChange: (config: GeneratedReportTableProps) => void;
}

interface DropdownChangeEvent {
  value: number;
}

const defaultLoadConf: IPeriodLoadingConfig = {
  defaultConfigUnit: ChronoUnitLegacy.HOURS,
  truncateStartUnit: ChronoUnitLegacy.DAYS,
  periodUnit: ChronoUnitLegacy.DAYS,
  period: -1,
  offsetUnit: ChronoUnitLegacy.HOURS,
  offset: 1,
};

const itemsPerPageOptions = [
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
];

const reportTypeOptions = Object.keys(ReportType).map(key => ({
  label: key,
  value: ReportType[key as keyof typeof ReportType],
}));

const chronoUnitOptions = Object.keys(ChronoUnitLegacy).map(key => ({
  label: key,
  value: ChronoUnitLegacy[key as keyof typeof ChronoUnitLegacy],
}));

function GeneratedReportTableEditor({
  config,
  onConfigChange,
}: ICommonGeneratedReportTableEditorProps) {
  const [currentConfig, setCurrentConfig] = useState(config);

  useEffect(() => {
    setCurrentConfig(config);
  }, [config]);

  const handleLoadConf = (e: { value: boolean }): void => {
    const tempConfig = structuredClone(currentConfig);
    if (e.value) {
      tempConfig.loadingConfig = defaultLoadConf;
    } else {
      delete tempConfig.loadingConfig;
    }
    setCurrentConfig(tempConfig);
    onConfigChange(tempConfig);
  };

  const handleLoadConfChange = (e: InputNumberValueChangeEvent | { value: number | string; target: { id: string } }) => {
    const key = e.target.id as keyof IPeriodLoadingConfig;
    const value = typeof e.value === "string" ? e.value.toUpperCase() : e.value;
    setCurrentConfig(prev => {
      const newLoadConf = {
        ...prev.loadingConfig,
        [key]: value,
      };
      const newConfig = {
        ...prev,
        loadingConfig: newLoadConf,
      };
      onConfigChange(newConfig);
      return newConfig;
    });
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-column gap-2">
        <label htmlFor="ids">IDs</label>
        <Chips
          id="ids"
          value={currentConfig.ids}
          onChange={(e: any) => onConfigChange({ ...currentConfig, ids: e.value })}
        />
      </div>
      <div className="flex flex-row gap-5">
        <div className="flex flex-column w-2 gap-2">
          <label htmlFor="itemsPerPage">{T("component.items-per-page")}</label>
          <Dropdown
            value={currentConfig.itemsPerPage}
            options={itemsPerPageOptions}
            onChange={(e: DropdownChangeEvent) =>
              onConfigChange({ ...currentConfig, itemsPerPage: e.value })
            }
          />
        </div>
        <div className="flex flex-column w-2 gap-2">
          <label htmlFor="type">{T("word.type")}</label>
          <Dropdown
            value={currentConfig.type}
            options={reportTypeOptions}
            onChange={(e: any) =>
              onConfigChange({
                ...currentConfig,
                type: e.value as ReportType,
              })
            }
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="showDateSelection">{T("word.date-selection")}</label>
          <InputSwitch
            checked={Boolean(currentConfig.showDateSelection)}
            onChange={e =>
              onConfigChange({
                ...currentConfig,
                showDateSelection: e.value,
              })
            }
          />
        </div>
      </div>
      <div>
        <div className="gap-2 flex flex-column justify-content-center align-items-start mb-3">
          <label htmlFor="loadConf">{T("load-conf.load-conf")}</label>
          <InputSwitch
            id="loadConf"
            checked={currentConfig.loadingConfig !== undefined}
            onChange={handleLoadConf}
          />
        </div>
        {currentConfig.loadingConfig !== undefined && (
          <div className="flex flex-column gap-3">
            <div className="flex flex-row gap-3">
              <div className="flex flex-column gap-2">
                <label htmlFor="defaultConfigUnit">{T("load-conf.default-config-unit")}</label>
                <Dropdown
                  id="defaultConfigUnit"
                  value={currentConfig.loadingConfig.defaultConfigUnit}
                  options={chronoUnitOptions}
                  onChange={handleLoadConfChange}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="truncateStartUnit">{T("load-conf.truncate-start-unit")}</label>
                <Dropdown
                  id="truncateStartUnit"
                  value={currentConfig.loadingConfig.truncateStartUnit}
                  options={chronoUnitOptions}
                  onChange={handleLoadConfChange}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="periodUnit">{T("load-conf.period-unit")}</label>
                <Dropdown
                  id="periodUnit"
                  value={currentConfig.loadingConfig.periodUnit}
                  options={chronoUnitOptions}
                  onChange={handleLoadConfChange}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="offsetUnit">{T("load-conf.offset-unit")}</label>
                <Dropdown
                  id="offsetUnit"
                  value={currentConfig.loadingConfig.offsetUnit}
                  options={chronoUnitOptions}
                  onChange={handleLoadConfChange}
                />
              </div>
            </div>
            <div className="flex flex-row gap-3">
              <div className="flex flex-column gap-2">
                <label htmlFor="period">{T("load-conf.period")}</label>
                <InputNumber
                  id="period"
                  value={currentConfig.loadingConfig.period as number}
                  onValueChange={handleLoadConfChange}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="offset">{T("load-conf.offset")}</label>
                <InputNumber
                  id="offset"
                  value={currentConfig.loadingConfig.offset as number}
                  onValueChange={handleLoadConfChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GeneratedReportTableEditor;
